import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Icon } from '@iconify/react';
import { decodeData, encodeData } from '../../../utils/encodeAndEncode';
import { getCourseAssessments } from '../../../services/adminService';

function CourseAnalyticsAssessment() {
  const navigate = useNavigate();
  const { courseId } = useParams();
  const decodedCourseId = decodeData(courseId);

  const [assessmentData, setAssessmentData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 0,
    totalCount: 0
  });

  // Fetch assessment data
  useEffect(() => {
    fetchAssessmentData();
  }, [decodedCourseId, search, pagination.page]);

  const fetchAssessmentData = async () => {
    try {
      setLoading(true);
      const response = await getCourseAssessments({
        course_id: decodedCourseId,
        search: search,
        page: pagination.page,
        limit: pagination.limit
      });

      // console.log('Assessment Data Response:', response);

      if (response.success && response.data) {
        setAssessmentData(response.data.assessments || []);
        setPagination(prev => ({
          ...prev,
          page: response.data.pagination?.current_page || 1,
          totalPages: response.data.pagination?.total_pages || 0,
          totalCount: response.data.pagination?.total_records || 0
        }));
      }
    } catch (error) {
      console.error('Error fetching assessment data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  const handleNavigateToDetails = (assessmentId) => {
    const encodedAssessmentId = encodeData(assessmentId);
    const encodedCourseId = encodeData(decodedCourseId);
    navigate(`/admin/courses/analytics/${encodedCourseId}/CourseAnalyticsAssessmentDetails/${encodedAssessmentId}`);
  };
  

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  return (
    <>
      {/* Search and Filter */}
      <div className="flex flex-col md:flex-row mb-3">
        <div className="md:w-1/2 mt-2 flex justify-start items-center">
          <input
            type="search"
            className="w-full max-w-[300px] px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
            placeholder="Search assessments..."
            value={search}
            onChange={handleSearchChange}
          />
        </div>
      </div>

      {/* Table */}
      <div className="mb-3">
        <div className="w-full">
          <div className="bg-white rounded-lg shadow-sm border border-gray-100">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="bg-gray-50 font-medium py-3 px-4 text-left">SL.No</th>
                    <th className="bg-gray-50 font-medium py-3 px-4 text-left">Assessment Name</th>
                    <th className="bg-gray-50 font-medium py-3 px-4 text-left">Questions</th>
                    <th className="bg-gray-50 font-medium py-3 px-4 text-left">Attempts</th>
                    <th className="bg-gray-50 font-medium py-3 px-4 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="5" className="text-center py-4">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary" role="status">
                          <span className="sr-only">Loading...</span>
                        </div>
                      </td>
                    </tr>
                  ) : assessmentData.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="text-center py-4 text-gray-500">
                        No assessments found
                      </td>
                    </tr>
                  ) : (
                    assessmentData.map((assessment, index) => (
                      <tr
                        key={assessment.assessment_id || index}
                        className="border-b hover:bg-gray-50 cursor-pointer"
                        onClick={() => handleNavigateToDetails(assessment.assessment_id)}
                      >
                        <td className="py-3 align-middle px-4">
                          {(pagination.page - 1) * pagination.limit + index + 1}
                        </td>
                        <td className="py-3 align-middle px-4">
                          <span className="font-medium">{assessment.assessment_name || 'N/A'}</span>
                        </td>
                        <td className="py-3 align-middle px-4">
                          {assessment.total_questions || 0}
                        </td>
                        <td className="py-3 align-middle px-4">
                          {assessment.total_attempts || 0}
                        </td>
                        <td className="py-3 align-middle px-4">
                          <div className="flex gap-2">
                            <button
                              className="border border-primary text-primary hover:bg-primary hover:text-white font-medium py-1 px-2 rounded text-sm"
                              title="View Details"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleNavigateToDetails(assessment.assessment_id);
                              }}
                            >
                              <Icon icon="fluent:eye-24-regular" width="16" height="16" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Pagination */}
      {!loading && assessmentData.length > 0 && (
        <div className="flex flex-col md:flex-row gap-3 mt-4 pt-4 border-t border-gray-100">
          <div className="md:w-1/2">
            <select
              className="w-auto px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
              value={pagination.limit}
              onChange={(e) => setPagination(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }))}
            >
              <option value={10}>10 records per page</option>
              <option value={25}>25 records per page</option>
              <option value={50}>50 records per page</option>
            </select>
          </div>
          <div className="md:w-1/2 flex justify-end items-center gap-3">
            <button
              className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ width: '150px' }}
              disabled={pagination.page <= 1}
              onClick={() => handlePageChange(pagination.page - 1)}
            >
              Prev
            </button>
            <span>Page {pagination.page} of {pagination.totalPages}</span>
            <button
              className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ width: '150px' }}
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => handlePageChange(pagination.page + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default CourseAnalyticsAssessment;
